import React from "react";
import useExcelUpload, {
  generateRandomAmount,
  generateRandomDescription,
  generateRandomReferences,
} from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";
import {
  investmentTaxAdjustmentOptions_tagging,
  taxAdjustmentOptions_tagging,
} from "../../../../utils/static/detailsPL";

const getTaxAdjustmentOptions = (companyType) =>
  companyType === "investment"
    ? investmentTaxAdjustmentOptions_tagging
    : taxAdjustmentOptions_tagging;

const getInstructions = (companyType) => [
  ["Col", "Comments"],
  ["Description", "Provide a clear description of each breakdown line item."],
  [
    "Amount",
    "Enter the monetary amount associated with each breakdown line. Use numeric values only.",
  ],
  [
    "Tax Adjustment",
    `Tax adjustment. Valid values are: ${getTaxAdjustmentOptions(companyType)
      .map((option) => option.label)
      .join(", ")}. If left blank, tax adjustment will be none.`,
  ],
  [
    "Reference",
    "You may include a reference or identifier for each breakdown line (e.g., invoice number, project code, etc.).",
  ],
];

const getTaxAdjustmentValue = (companyType, taxAdjustment) => {
  if (
    taxAdjustment === "" ||
    taxAdjustment === undefined ||
    taxAdjustment === "None"
  ) {
    return "";
  }

  if (["Deduct", "Added Back"].includes(taxAdjustment)) {
    return taxAdjustment;
  }

  return (
    getTaxAdjustmentOptions(companyType).find(
      (option) => option.label === taxAdjustment
    )?.value || ""
  );
};

const BreakdownExcelUpload = ({
  company,
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const taxAdjustmentLabels = getTaxAdjustmentOptions(company.companyType).map(
    (option) => option.label
  );

  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: `breakdown_schedule_upload_template.xlsx`,
    instructions: getInstructions(company.companyType),
    sampleData: Array.from({ length: 10 }, (_) => ({
      Description: generateRandomDescription(),
      Amount: generateRandomAmount(),
      "Tax Adjustment":
        taxAdjustmentLabels[
          Math.floor(Math.random() * taxAdjustmentLabels.length)
        ],
      Reference: generateRandomReferences(),
    })),
    validationSchema: [
      { type: "text", required: true },
      { type: "number", round: true, required: true },
      {
        type: "",
        fixedValue: taxAdjustmentLabels.concat("", undefined),
      },
      { type: "text" },
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const updatedData = [];

        excelData.forEach((row) => {
          const [Description, Amount, TaxAdjustment, Reference] = row;

          updatedData.push({
            name: Description,
            amount: Math.round(Amount),
            taxAdjustment: getTaxAdjustmentValue(
              company.companyType,
              TaxAdjustment
            ),
            reference: Reference,
          });
        });

        onExcelAddRows(updatedData);
      }
    },
  });

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions(company.companyType)}
      fixedValueGroups={[
        {
          label: "Tax Adjustment",
          values: ["", ...taxAdjustmentLabels],
        },
      ]}
    />
  );
};

export default BreakdownExcelUpload;
